import { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import SocketV2Service from '../services/socket/socket-v2.service';
import { SocketAckResponse } from '../services/socket/socket.types';

interface UseSocketV2Options {
  autoConnect?: boolean;
  reconnectOnMount?: boolean;
  autoJoinRoom?: boolean;
  token?: string;
  eventId?: string;
  query?: Record<string, any>;
  auth?: Record<string, any>;
}

interface UseSocketV2Return {
  emit: (event: string, ...args: any[]) => void;
  emitWithAck: <T = any>(
    event: string,
    data: any,
    timeout?: number
  ) => Promise<SocketAckResponse<T>>;
  on: <T = any>(event: string, handler: (data: T) => void) => void;
  once: <T = any>(event: string, handler: (data: T) => void) => void;
  off: (event: string, handler?: (...args: any[]) => void) => void;
  isConnected: boolean;
  socketId: string | undefined;
  reconnectAttempts: number;
  instanceId: number;
  connect: () => void;
  disconnect: () => void;
  joinRoom: (room: string) => void;
  leaveRoom: (room: string) => void;
  joinRoomWithAck: (room: string) => Promise<SocketAckResponse>;
  joinEventWithAck: (eventId: string) => Promise<SocketAckResponse>;
  leaveRoomWithAck: (room: string) => Promise<SocketAckResponse>;
}

export function useSocketV2(
  url?: string,
  options: UseSocketV2Options = {}
): UseSocketV2Return {
  const { 
    autoConnect = true, 
    reconnectOnMount = false,
    autoJoinRoom = true,
    token,
    eventId,
    query,
    auth,
  } = options;
  
  const [isConnected, setIsConnected] = useState(false);
  const [socketId, setSocketId] = useState<string | undefined>(undefined);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  
  const socketServiceRef = useRef<SocketV2Service | null>(null);
  const isServiceReadyRef = useRef<boolean>(false);
  const handlersRef = useRef<Map<string, Array<(...args: any[]) => void>>>(
    new Map()
  );
  const hasJoinedRoomRef = useRef<boolean>(false);

  const queryString = useMemo(() => JSON.stringify(query), [query]);
  const authString = useMemo(() => JSON.stringify(auth), [auth]);

  useEffect(() => {
    if (!url) return;

    SocketV2Service.resetInstance();

    try {
      const queryParams: Record<string, any> = { ...query };
      if (eventId) {
        queryParams.eventId = eventId;
      }
      const authParams: Record<string, any> = { ...auth };
      if (token) {
        authParams.token = token;
      }

      socketServiceRef.current = SocketV2Service.getInstance({
        url,
        options: {
          path: '/ws/',
          transports: ['websocket', 'polling'] as Array<'websocket' | 'polling'>,
          ...(Object.keys(queryParams).length > 0 && { query: queryParams }),
          ...(Object.keys(authParams).length > 0 && { auth: authParams }),
        },
      });

      isServiceReadyRef.current = true;

      if (autoConnect || reconnectOnMount) {
        socketServiceRef.current.connect();
      }
    } catch (error) {
      console.error('[useSocketV2] Initialization error:', error);
      isServiceReadyRef.current = false;
    }

    const handlers = handlersRef.current;
    const socketService = socketServiceRef.current;

    return () => {
      isServiceReadyRef.current = false;
      socketService?.disconnect();
      handlers.forEach((handlerList, event) => {
        handlerList.forEach(handler => {
          socketService?.off(event, handler);
        });
      });
      handlers.clear();
    };
  }, [url, autoConnect, reconnectOnMount, token, eventId, queryString, authString, query, auth]);

  useEffect(() => {
    if (!socketServiceRef.current) return;

    const handleConnect = () => {
      setIsConnected(true);
      const id = socketServiceRef.current?.getSocketId();
      setSocketId(id);
      setReconnectAttempts(0);
      hasJoinedRoomRef.current = false;
    };

    const handleDisconnect = () => {
      setIsConnected(false);
      setSocketId(undefined);
      hasJoinedRoomRef.current = false;
    };

    const handleReconnectAttempt = () => {
      const attempts = socketServiceRef.current?.getReconnectAttempts() ?? 0;
      setReconnectAttempts(attempts);
    };


    socketServiceRef.current.on('connect', handleConnect);
    socketServiceRef.current.on('disconnect', handleDisconnect);
    socketServiceRef.current.on('reconnect_attempt', handleReconnectAttempt);

    setIsConnected(socketServiceRef.current.isConnected());
    setSocketId(socketServiceRef.current.getSocketId());

    return () => {
      socketServiceRef.current?.off('connect', handleConnect);
      socketServiceRef.current?.off('disconnect', handleDisconnect);
      socketServiceRef.current?.off('reconnect_attempt', handleReconnectAttempt);
    };
  }, [autoJoinRoom, eventId]);

  const emit = useCallback((event: string, ...args: any[]) => {
    socketServiceRef.current?.emit(event, ...args);
  }, []);

  const emitWithAck = useCallback(
    async <T = any>(
      event: string,
      data: any,
      timeout?: number
    ): Promise<SocketAckResponse<T>> => {
      if (!socketServiceRef.current) {
        return {
          success: false,
          error: 'Socket service not initialized',
        };
      }
      return socketServiceRef.current.emitWithAck<T>(event, data, timeout);
    },
    []
  );

  const on = useCallback(<T = any>(event: string, handler: (data: T) => void) => {
    if (!isServiceReadyRef.current || !socketServiceRef.current) {
      console.warn('[useSocketV2] Cannot add listener - service not ready or not initialized');
      return;
    }

    if (!handlersRef.current.has(event)) {
      handlersRef.current.set(event, []);
    }
    handlersRef.current.get(event)?.push(handler);

    socketServiceRef.current.on(event, handler);
  }, []);

  const once = useCallback(
    <T = any>(event: string, handler: (data: T) => void) => {
      socketServiceRef.current?.once(event, handler);
    },
    []
  );

  const off = useCallback(
    (event: string, handler?: (...args: any[]) => void) => {
      if (handler) {
        const handlers = handlersRef.current.get(event);
        if (handlers) {
          const index = handlers.indexOf(handler);
          if (index > -1) {
            handlers.splice(index, 1);
          }
          if (handlers.length === 0) {
            handlersRef.current.delete(event);
          }
        }
      } else {
        handlersRef.current.delete(event);
      }

      socketServiceRef.current?.off(event, handler);
    },
    []
  );

  const connect = useCallback(() => {
    socketServiceRef.current?.connect();
  }, []);

  const disconnect = useCallback(() => {
    socketServiceRef.current?.disconnect();
    setIsConnected(false);
    setSocketId(undefined);
  }, []);

  const joinRoom = useCallback((room: string) => {
    socketServiceRef.current?.joinRoom(room);
  }, []);

  const leaveRoom = useCallback((room: string) => {
    socketServiceRef.current?.leaveRoom(room);
  }, []);

  const joinRoomWithAck = useCallback(
    async (room: string): Promise<SocketAckResponse> => {
      if (!socketServiceRef.current) {
        return {
          success: false,
          error: 'Socket service not initialized',
        };
      }
      return socketServiceRef.current.joinRoomWithAck(room);
    },
    []
  );

  const joinEventWithAck = useCallback(
    async (eventId: string): Promise<SocketAckResponse> => {
      if (!socketServiceRef.current) {
        return {
          success: false,
          error: 'Socket service not initialized',
        };
      }
      return socketServiceRef.current.joinEventWithAck(eventId);
    },
    []
  );

  const leaveRoomWithAck = useCallback(
    async (room: string): Promise<SocketAckResponse> => {
      if (!socketServiceRef.current) {
        return {
          success: false,
          error: 'Socket service not initialized',
        };
      }
      return socketServiceRef.current.leaveRoomWithAck(room);
    },
    []
  );

  return {
    emit,
    emitWithAck,
    on,
    once,
    off,
    isConnected,
    socketId,
    reconnectAttempts,
    instanceId: 0, // useSocketV2 gerencia sua própria instância
    connect,
    disconnect,
    joinRoom,
    leaveRoom,
    joinRoomWithAck,
    joinEventWithAck,
    leaveRoomWithAck,
  };
}

export function useSocketV2Instance(): UseSocketV2Return {
  const [isConnected, setIsConnected] = useState(false);
  const [socketId, setSocketId] = useState<string | undefined>(undefined);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [instanceId, setInstanceId] = useState(0);
  
  const socketServiceRef = useRef<SocketV2Service | null>(null);
  const handlersRef = useRef<Map<string, Array<(...args: any[]) => void>>>(
    new Map()
  );
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    // Polling contínuo para monitorar mudanças na instância
    const checkInstance = () => {
      const hasInstance = SocketV2Service.hasInstance();
      
      if (!hasInstance && socketServiceRef.current !== null) {
        console.log('[useSocketV2Instance] Instância foi destruída');
        socketServiceRef.current = null;
        setIsConnected(false);
        setSocketId(undefined);
        setInstanceId(prev => prev + 1);
        return;
      }
      
      if (hasInstance) {
        const instance = SocketV2Service.getInstance();
        
        // Se é uma instância diferente, atualizar referência
        if (socketServiceRef.current !== instance) {
          console.log('[useSocketV2Instance] Nova instância detectada');
          socketServiceRef.current = instance;
          
          // Atualizar estado de conexão
          setIsConnected(instance.isConnected());
          setSocketId(instance.getSocketId());
          setInstanceId(prev => prev + 1);
        }
      }
    };

    // Verificar imediatamente
    checkInstance();

    // Verificar a cada 500ms
    intervalRef.current = setInterval(checkInstance, 500);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!socketServiceRef.current) return;

    const handleConnect = () => {
      setIsConnected(true);
      const id = socketServiceRef.current?.getSocketId();
      setSocketId(id);
      setReconnectAttempts(0);
    };

    const handleDisconnect = () => {
      setIsConnected(false);
      setSocketId(undefined);
    };

    const handleReconnectAttempt = () => {
      const attempts = socketServiceRef.current?.getReconnectAttempts() ?? 0;
      setReconnectAttempts(attempts);
    };

    socketServiceRef.current.on('connect', handleConnect);
    socketServiceRef.current.on('disconnect', handleDisconnect);
    socketServiceRef.current.on('reconnect_attempt', handleReconnectAttempt);

    // Capturar o estado atual
    setIsConnected(socketServiceRef.current.isConnected());
    setSocketId(socketServiceRef.current.getSocketId());

    const currentInstance = socketServiceRef.current;

    return () => {
      currentInstance?.off('connect', handleConnect);
      currentInstance?.off('disconnect', handleDisconnect);
      currentInstance?.off('reconnect_attempt', handleReconnectAttempt);
    };
  }, [instanceId]);

  const emit = useCallback((event: string, ...args: any[]) => {
    socketServiceRef.current?.emit(event, ...args);
  }, []);

  const emitWithAck = useCallback(
    async <T = any>(
      event: string,
      data: any,
      timeout?: number
    ): Promise<SocketAckResponse<T>> => {
      if (!socketServiceRef.current) {
        return {
          success: false,
          error: 'Socket service not initialized',
        };
      }
      return socketServiceRef.current.emitWithAck<T>(event, data, timeout);
    },
    []
  );

  const on = useCallback(<T = any>(event: string, handler: (data: T) => void) => {
    if (!socketServiceRef.current) {
      console.warn('[useSocketV2Instance] Cannot add listener - service not initialized');
      return;
    }

    socketServiceRef.current.on(event, handler);
  }, []);

  const once = useCallback(
    <T = any>(event: string, handler: (data: T) => void) => {
      socketServiceRef.current?.once(event, handler);
    },
    []
  );

  const off = useCallback(
    (event: string, handler?: (...args: any[]) => void) => {
      socketServiceRef.current?.off(event, handler);
    },
    []
  );

  const connect = useCallback(() => {
    socketServiceRef.current?.connect();
  }, []);

  const disconnect = useCallback(() => {
    socketServiceRef.current?.disconnect();
    setIsConnected(false);
    setSocketId(undefined);
  }, []);

  const joinRoom = useCallback((room: string) => {
    socketServiceRef.current?.joinRoom(room);
  }, []);

  const leaveRoom = useCallback((room: string) => {
    socketServiceRef.current?.leaveRoom(room);
  }, []);

  const joinRoomWithAck = useCallback(
    async (room: string): Promise<SocketAckResponse> => {
      if (!socketServiceRef.current) {
        return {
          success: false,
          error: 'Socket service not initialized',
        };
      }
      return socketServiceRef.current.joinRoomWithAck(room);
    },
    []
  );

  const joinEventWithAck = useCallback(
    async (eventId: string): Promise<SocketAckResponse> => {
      if (!socketServiceRef.current) {
        return {
          success: false,
          error: 'Socket service not initialized',
        };
      }
      return socketServiceRef.current.joinEventWithAck(eventId);
    },
    []
  );

  const leaveRoomWithAck = useCallback(
    async (room: string): Promise<SocketAckResponse> => {
      if (!socketServiceRef.current) {
        return {
          success: false,
          error: 'Socket service not initialized',
        };
      }
      return socketServiceRef.current.leaveRoomWithAck(room);
    },
    []
  );

  return {
    emit,
    emitWithAck,
    on,
    once,
    off,
    isConnected,
    socketId,
    reconnectAttempts,
    instanceId,
    connect,
    disconnect,
    joinRoom,
    leaveRoom,
    joinRoomWithAck,
    joinEventWithAck,
    leaveRoomWithAck,
  };
}

